import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Menu, MenuItem, Divider } from '@mui/material'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { listPortfoliosRoutine } from 'ducks/portfolio/actions'
import { selectPortfolioList } from 'ducks/portfolio/selectors'
import { addBuildingPassports, createPortfolio } from 'services/portfolioService'
import { font, ink, line, radius, surface, transition } from 'features/dashboard/tokens'

const AddToPortfolioMenu = ({ bpId, bpName, currentPortfolioId = null, onAssigned }) => {
  const dispatch = useDispatch()
  const portfolios = useSelector(selectPortfolioList)
  const [anchor, setAnchor] = useState(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (anchor) dispatch(listPortfoliosRoutine())
  }, [anchor, dispatch])

  const open = event => {
    event.stopPropagation()
    setAnchor(event.currentTarget)
  }

  const close = () => {
    setAnchor(null)
    setCreating(false)
    setNewName('')
  }

  const options = useMemo(
    () => (portfolios || []).filter(portfolio => String(portfolio.id) !== String(currentPortfolioId)),
    [portfolios, currentPortfolioId]
  )

  const assign = async portfolioId => {
    if (busy) return
    setBusy(true)
    try {
      await addBuildingPassports(portfolioId, [bpId])
      toast.success('Added to the portfolio.')
      close()
      onAssigned && onAssigned()
    } catch (error) {
      toast.error('Could not add to the portfolio.')
    } finally {
      setBusy(false)
    }
  }

  const createAndAssign = async () => {
    const trimmed = newName.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      await createPortfolio({ name: trimmed, bpIds: [bpId] })
      toast.success('Portfolio created and building added.')
      dispatch(listPortfoliosRoutine())
      close()
      onAssigned && onAssigned()
    } catch (error) {
      toast.error('Could not create the portfolio.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Trigger
        type='button'
        onClick={open}
        title='Add to a portfolio'
        aria-label={`Add ${bpName || 'this Building Passport'} to a portfolio`}
      >
        <AccountTreeOutlinedIcon style={{ fontSize: 20 }} />
        <SidePlus aria-hidden='true'>
          <AddRoundedIcon style={{ fontSize: 12 }} />
        </SidePlus>
      </Trigger>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={event => event.stopPropagation()}
        disableAutoFocusItem
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {creating
          ? (
            <CreateBox onKeyDown={event => event.stopPropagation()}>
              <CreateLabel>New portfolio</CreateLabel>
              <CreateInput
                autoFocus
                placeholder='Portfolio name'
                value={newName}
                onChange={event => setNewName(event.target.value)}
                onKeyDown={event => { if (event.key === 'Enter') createAndAssign() }}
              />
              <CreateActions>
                <MiniButton type='button' disabled={busy || !newName.trim()} onClick={createAndAssign}>
                  {busy ? 'Adding…' : 'Create & add'}
                </MiniButton>
                <MiniGhost type='button' onClick={() => { setCreating(false); setNewName('') }}>
                  Cancel
                </MiniGhost>
              </CreateActions>
            </CreateBox>
          )
          : [
            <MenuCaption key='caption'>Add to portfolio</MenuCaption>,
            ...(options.length === 0
              ? [<MenuItem key='none' disabled>No other portfolios yet</MenuItem>]
              : options.map(portfolio => (
                <MenuItem key={portfolio.id} disabled={busy} onClick={() => assign(portfolio.id)}>
                  {portfolio.name}
                </MenuItem>
              ))),
            <Divider key='divider' />,
            <MenuItem key='new' onClick={() => setCreating(true)}>
              <NewRow><AddRoundedIcon style={{ fontSize: 16 }} /> Create new portfolio…</NewRow>
            </MenuItem>,
          ]}
      </Menu>
    </>
  )
}

export default AddToPortfolioMenu

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.mute};
  cursor: pointer;
  transition: ${transition};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    background: ${surface.wash};
  }
`

const SidePlus = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  flex: 0 0 auto;
`

const MenuCaption = styled.div`
  padding: 6px 16px 4px;
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const NewRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`

const CreateBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 14px;
  min-width: 240px;
`

const CreateLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const CreateInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  font: inherit;
  font-size: ${font.body};
  outline: none;
  box-sizing: border-box;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`

const CreateActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MiniButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: ${radius.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font: inherit;
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: default; }
`

const MiniGhost = styled.button`
  padding: 8px 10px;
  border: none;
  background: transparent;
  color: ${ink.mute};
  font: inherit;
  font-size: ${font.caption};
  font-weight: 600;
  cursor: pointer;

  &:hover { color: ${ink.base}; }
`
